import { ApplicationService, MigrationService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ApplicationServerMapping, DeserializedApplication, ServersStrategy } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Spinner } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { ColumnOrderType } from '../portfolio-data-asset/constants';
import { SchemaPropertyMap } from '../portfolio-data-asset/types';
import { generateColumnDefs } from '../portfolio-data-asset/utils';
import { applicationResourceName } from './constants';
import { ApplicationWithMigrationStrategy, ServerWithMigrationStrategy } from './interfaces';

type Props = FlashContextInterface & {
    scoredServers: ServerWithMigrationStrategy[];
}

const EXCLUDED_COLUMNS = ['applicationId', 'businessOwnerId', 'databaseMappings', 'isOverridden', 'migrationPlan', 'migrationStrategy',
    'portfolioId', 'ranking', 'serverIds', 'serverMappings', 'score'];
const applicationService = new ApplicationService();
const migrationService = new MigrationService();
const schemaService = new SchemaService();

function MigrationStrategyApplicationTable({ scoredServers, flash }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const portfolioId = match?.params?.portfolioId;

    const isMounted = useIsMounted();
    const [ loading, setLoading ] = useState(false);
    const [ applicationItems, setApplicationItems ] = useState<ApplicationWithMigrationStrategy[]>([]);
    const [ applicationColumnDefinitions, setApplicationColumnDefinitions ] = useState<ColumnDefinition<ApplicationWithMigrationStrategy>[]>([]);

    const loadApplications = async () => {
        if (!scoredServers || !portfolioId || !isMounted()) {
            return;
        }
        setLoading(true);
        try {
            const applications = await applicationService.list(portfolioId) as DeserializedApplication[];

            const applicationServerStrategy: ServersStrategy[] = applications.map(application => {
                const serversStrategy: {strategy: string;}[] = !application.serverMappings ? [] : 
                    application.serverMappings.reduce((strategies: {strategy: string;}[], mapping: ApplicationServerMapping ) => {
                        const found = scoredServers.find(server => mapping.serverId === server.serverId);
                        if (found && found.migrationStrategy) {
                            strategies.push({strategy: found.migrationStrategy});
                        }
                        return strategies;
                    }, []);
                return {
                    id: application.applicationId,
                    serversStrategy: serversStrategy
                };
            });

            const applicationStrategies = await migrationService.recommendedStrategy({applications: applicationServerStrategy});

            const applicationsWithMigrationStrategy: ApplicationWithMigrationStrategy[] = applications.map(application => {
                let strategy: string = applicationStrategies[application.applicationId] ?? constants.UNKNOWN;
                let isOverridden = false;
                if (application.migrationPlan) {
                    strategy = application.migrationPlan;
                    isOverridden = true;
                }
                return {...application, migrationStrategy: strategy, isOverridden: isOverridden};
            });
            if (!isMounted()) {
                return;
            }
            setApplicationItems(applicationsWithMigrationStrategy);

            const applicationSchema = await schemaService.listAttributes(applicationResourceName, portfolioId);

            const applicationHeaders: SchemaPropertyMap = {};
            applicationSchema.forEach(appSchema => {
                applicationHeaders[appSchema.value] = appSchema;
            });

            const columnDefinitions = generateColumnDefs(applicationsWithMigrationStrategy, applicationHeaders, ColumnOrderType.APPLICATION, EXCLUDED_COLUMNS);
            const columns: ColumnDefinition<ApplicationWithMigrationStrategy>[] = [
                {
                    header: constants.APPLICATION_ID,
                    type: ColumnTypes.TEXT,
                    id: 'applicationId',
                    cell: item => item.applicationId,
                    sortingField: 'applicationId',
                    text: constants.APPLICATION_ID,
                },
                {
                    header: constants.MIGRATION_STRATEGY,
                    type: ColumnTypes.TEXT, 
                    id: 'migrationStrategy',
                    cell: item => item.migrationStrategy,
                    sortingField: 'migrationStrategy',
                    text: constants.MIGRATION_STRATEGY,
                },
                {
                    header: constants.OVERRIDDEN,
                    type: ColumnTypes.BOOLEAN,
                    id: 'isOverridden',
                    data: item => item.isOverridden ? constants.TRUE : constants.FALSE,
                    cell: item => item.isOverridden ? constants.TRUE : constants.FALSE,
                    sortingField: 'isOverridden',
                    text: constants.OVERRIDDEN,
                }
            ];
            if (isMounted()) {
                setApplicationColumnDefinitions(columns.concat(columnDefinitions));
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.APPLICATION_ERROR_LOADING);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scoredServers]);

    const defaultSortingState: SortingState<ApplicationWithMigrationStrategy> = {
        isDescending: false,
        sortingColumn: {
            sortingField: 'applicationId',
        },
    };

    return loading ? <Spinner size='big'/> : (
        <AWSCATTable 
            resourceName={constants.APPLICATION_RESOURCE_NAME}
            items={applicationItems}
            columnDefinitions={applicationColumnDefinitions}
            enableAutoSorting
            defaultSortBy={defaultSortingState}
            autoGenerateVisibleContent
            useDefaultDownload
        />
    );
}

export default withFlashContext(MigrationStrategyApplicationTable);
